import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';
import useReduxState from '@portal/hooks/useReduxState';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LimitsActions } from '@portal/actions';
import { moneyFormat } from '@portal/utils/format';
import {
  TransactionLimitType,
  PeriodType,
  TransactionLimitStatusType,
  TransactionType,
} from '@portal/models/enumerators';
import LimitPixModal from '@portal/components/LimitPixModal/LimitPixModal';

import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import { Chip } from '@mui/material';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import * as MessageService from '~/services/message';
import { UPDATE_LIMIT_STATUS } from '@portal/actions/actionTypes';
import { CardLimitsProps } from '@portal/models/bank';
import AccessListControl from '@portal/components/AccessListControl/AccessListControl';
import { getLimitFlowPermissions } from '@portal/utils/transaction';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import { MoonOutlined, SlidersOutlined, SunOutlined } from '@ant-design/icons';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';

type LimitsProps = {
  transactionType: TransactionType;
};

const Limits = ({ transactionType }: LimitsProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const {
    limit: { list, status },
  } = useReduxState();

  const [visible, setVisible] = useState(false);
  const [pinModal, setPinModal] = useState(false);
  const [permanentLimit, setPermanentLimit] =
    useState<bank.TransactionLimit | null>({
      amount: 0,
      limitType: TransactionLimitType.TRANSACTION,
      transactionType: transactionType,
      periodType: PeriodType.DAILY,
    });
  const [dayPeriodLimit, setDayPeriodLimit] = useState(0);
  const [dayPeriodLimitStatus, setDayPeriodLimitStatus] = useState<
    number | null
  >(0);
  const [dayTransactionLimit, setDayTransactionLimit] = useState(0);
  const [dayTransactionLimitStatus, setDayTransactionLimitStatus] = useState<
    number | null
  >(0);
  const [nightPeriodLimit, setNightPeriodLimit] = useState(0);
  const [nightPeriodLimitStatus, setNightPeriodLimitStatus] = useState<
    number | null
  >(0);
  const [nightTransactionLimit, setNightTransactionLimit] = useState(0);
  const [nightTransactionLimitStatus, setNightTransactionLimitStatus] =
    useState<number | null>(0);

  const filteredList = list?.filter(
    (limit: bank.TransactionLimit) => limit.transactionType === transactionType
  );

  useEffect(() => {
    if (filteredList) {
      const dayLimit = filteredList.find(
        (limit) =>
          limit.periodType === PeriodType.DAILY &&
          limit.limitType === TransactionLimitType.PERIOD
      );

      const transactionLimitDay = filteredList.find(
        (limit) =>
          limit.periodType === PeriodType.DAILY &&
          limit.limitType === TransactionLimitType.TRANSACTION
      );

      const nightLimit = filteredList.find(
        (limit) =>
          limit.periodType === PeriodType.NIGHTLY &&
          limit.limitType === TransactionLimitType.PERIOD
      );

      const transactionLimitNight = filteredList.find(
        (limit) =>
          limit.periodType === PeriodType.NIGHTLY &&
          limit.limitType === TransactionLimitType.TRANSACTION
      );
      if (dayLimit) {
        setDayPeriodLimit(dayLimit.amount ?? 0);

        if (dayLimit?.status) {
          setDayPeriodLimitStatus(dayLimit?.status);
        }
      }
      if (nightLimit) {
        setNightPeriodLimit(nightLimit.amount ?? 0);

        if (nightLimit.status) {
          setNightPeriodLimitStatus(nightLimit.status);
        }
      }
      if (transactionLimitDay) {
        setDayTransactionLimit(transactionLimitDay.amount ?? 0);
        if (transactionLimitDay.status) {
          setDayTransactionLimitStatus(transactionLimitDay.status);
        }
      }
      if (transactionLimitNight) {
        setNightTransactionLimit(transactionLimitNight.amount ?? 0);

        if (transactionLimitNight.status) {
          setNightTransactionLimitStatus(transactionLimitNight.status);
        }
      }
    }
  }, [filteredList]);

  const isDisabled = (status) => {
    if (
      [
        TransactionLimitStatusType.SEND_SOLICITATION,
        TransactionLimitStatusType.PENDING,
      ].includes(status)
    ) {
      return true;
    }
    return false;
  };

  const getLimitLabel = (limitType: TransactionLimitType) => {
    switch (limitType) {
      case TransactionLimitType.PERIOD:
        return translate('PAGES.LIMITS.LABELS.PERIOD_LIMIT');

      case TransactionLimitType.TRANSACTION:
        return translate('PAGES.LIMITS.LABELS.TRANSACTION_LIMIT');

      default:
        return '';
    }
  };

  const getTransactionTypeLabel = (transactionType: TransactionType) => {
    switch (transactionType) {
      case TransactionType.BILLET:
        return 'BILLET';
      case TransactionType.TED:
        return 'TED';
      case TransactionType.PIX:
        return 'PIX';
      case TransactionType.P2P:
        return 'P2P';
      case TransactionType.API_GATEWAY_PIX:
        return 'API_GATEWAY_PIX';
      default:
        return '';
    }
  };

  const handleLimitUpdate = (newLimit: number) => {
    if (newLimit === 0) {
      MessageService.error('LIMIT_CANNOT_BE_ZERO');
      return;
    }

    if (!permanentLimit) return;

    const samePeriodLimit = filteredList.find(
      (limit) =>
        limit.periodType === permanentLimit.periodType &&
        limit.limitType !== permanentLimit.limitType
    );

    if (
      permanentLimit.limitType === TransactionLimitType.TRANSACTION &&
      newLimit > Number(samePeriodLimit?.amount)
    ) {
      MessageService.error('LIMIT_TRANSACTION_EXCEED_LIMIT_PERIOD');
      return;
    }

    if (
      permanentLimit.limitType === TransactionLimitType.PERIOD &&
      newLimit < Number(samePeriodLimit?.amount)
    ) {
      MessageService.error('LIMIT_PERIOD_EXCEED_LIMIT_TRANSACTION');
      return;
    }

    setPermanentLimit((prevState) => {
      if (prevState) {
        return {
          ...prevState,
          amount: newLimit,
        };
      }
      return null;
    });

    setPinModal(true);
  };

  const confirmNewLimit = async (pin: string) => {
    setPinModal(false);
    if (pin && permanentLimit) {
      const encryptedPin = await encrypt(pin);

      dispatch(
        LimitsActions.updateLimit(
          {
            ...permanentLimit,
            amount: permanentLimit.amount,
            transactionType,
          },
          encryptedPin
        )
      );
    }
  };

  const transactionTypeLabel = getTransactionTypeLabel(transactionType);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      dispatch(LimitsActions.getAllLimits());
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (status === TransactionLimitStatusType.SUCCESS) {
      MessageService.success('PAGES.LIMITS.MESSAGE.ALTER_LIMIT_SUCCESS');
      dispatch({
        type: UPDATE_LIMIT_STATUS,
        payload: null,
      });
    }

    if (status === TransactionLimitStatusType.DENIED) {
      MessageService.error('PAGES.LIMITS.MESSAGE.ALTER_LIMIT_DENIED');
    }

    if (
      status === TransactionLimitStatusType.SEND_SOLICITATION ||
      status === TransactionLimitStatusType.PENDING
    ) {
      MessageService.warn('PAGES.LIMITS.MESSAGE.ALTER_LIMIT_PENDING');
      dispatch({
        type: UPDATE_LIMIT_STATUS,
        payload: null,
      });
    } else {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleButtonModal = ({
    limitActual,
    limitType,
    periodType,
  }: CardLimitsProps) => {
    setVisible(true);

    setPermanentLimit({
      limitType: limitType,
      transactionType: transactionType,
      amount: limitActual,
      periodType: periodType,
    });
  };

  const RenderCard = ({
    limitActual,
    limitType,
    status,
    periodType,
  }: CardLimitsProps) => (
    <div className="limits__card__box">
      <div className="limits__card__box__row">
        <div>
          <ThemedText variant="h4">{getLimitLabel(limitType)}</ThemedText>
          <ThemedText variant="body" className="limits__card__box__row__amount">
            {moneyFormat(limitActual)}
          </ThemedText>
        </div>
        <div className="limits__card__box__actions">
          {(status === TransactionLimitStatusType.SEND_SOLICITATION ||
            status === TransactionLimitStatusType.PENDING) && (
            <Chip
              label={translate(
                'PAGES.LIMITS.MESSAGE.ALTER_LIMIT_PENDING_HEADER'
              )}
              variant="outlined"
            />
          )}
          <AccessListControl
            accessRole={getLimitFlowPermissions(transactionType).write}
          >
            <AdvancedButton
              text={translate('PAGES.LIMITS.BUTTONS.DEFINE')}
              onClick={() =>
                handleButtonModal({
                  limitActual,
                  limitType,
                  periodType,
                  status,
                })
              }
              variant="outlined"
              disabled={isDisabled(status)}
            />
          </AccessListControl>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.MY_LIMITS'),
            url: getRouteStackPath('LIMITS', 'LIMITS_PAGE'),
          },
          {
            active: false,
            title: translate(
              `PAGES.MY_ACCOUNT.TABS.LIMITS_${transactionTypeLabel}`
            ),
          },
        ]}
      />
      <div className="limits">
        <AdvancedPanelContentHeader
          testId="limits-header"
          title={translate(`PAGES.LIMITS.TITLE_${transactionTypeLabel}`)}
          description={translate(
            `PAGES.LIMITS.DESCRIPTION_${transactionTypeLabel}`
          )}
          goBackAction={() => navigate.back()}
        />
        <div className="limits__card">
          <div className="limits__card__box__actions">
            <div className="limits__card__box__actions__icon">
              <SlidersOutlined />
            </div>
            <div>
              <ThemedText variant="h4">
                {translate(`PAGES.LIMITS.DESCRIPTION_${transactionTypeLabel}`)}
              </ThemedText>
            </div>
          </div>
          <ThemedText
            variant="body"
            className="limits__card__box__actions__description"
          >
            {translate('PAGES.LIMITS.HEADER_TEXT')}
          </ThemedText>
          <div className="limits__card__info">
            {translate('PAGES.LIMITS.SECOND_DESCRIPTION')}{' '}
            {transactionTypeLabel}
          </div>
        </div>
        <div className="limits__card">
          <div className="limits__card__header">
            <div className="limits__card__header__dayIcon">
              <SunOutlined />
            </div>
            <ThemedText variant="h4">
              {translate('PAGES.LIMITS.LABELS.DAYTIME')}
            </ThemedText>
          </div>
          {RenderCard({
            limitActual: dayPeriodLimit,
            periodType: PeriodType.DAILY,
            limitType: TransactionLimitType.PERIOD,
            status: dayPeriodLimitStatus,
          })}
          {RenderCard({
            limitActual: dayTransactionLimit,
            periodType: PeriodType.DAILY,
            limitType: TransactionLimitType.TRANSACTION,
            status: dayTransactionLimitStatus,
          })}
        </div>
        <div className="limits__card">
          <div className="limits__card__header">
            <div className="limits__card__header__nightIcon">
              <MoonOutlined />
            </div>
            <ThemedText variant="h4">
              {translate('PAGES.LIMITS.LABELS.NIGHTTIME')}
            </ThemedText>
          </div>
          {RenderCard({
            limitActual: nightPeriodLimit,
            periodType: PeriodType.NIGHTLY,
            limitType: TransactionLimitType.PERIOD,
            status: nightPeriodLimitStatus,
          })}
          {RenderCard({
            limitActual: nightTransactionLimit,
            periodType: PeriodType.NIGHTLY,
            limitType: TransactionLimitType.TRANSACTION,
            status: nightTransactionLimitStatus,
          })}
        </div>
        <ConfirmationModal
          visible={pinModal}
          setVisible={setPinModal}
          handleConfirmation={(pin: string) => confirmNewLimit(pin)}
        />
        <LimitPixModal
          title={translate('PAGES.LIMITS.MODAL.TITLE')}
          visible={visible}
          onCloseModal={() => {
            setVisible(false);
          }}
          onHandleUpdate={(rawValue: number) => handleLimitUpdate(rawValue)}
        />
      </div>
    </>
  );
};

export default Limits;
